import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaPhone, FaEnvelope } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { language, changeLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { key: 'navbar.home', path: '/' },
    { key: 'navbar.about', path: '/about' },
    { key: 'navbar.menu', path: '/menu' },
    { key: 'navbar.gallery', path: '/gallery' },
    { key: 'navbar.contact', path: '/contact' },
  ];

  return (
    <>
      {/* Top Bar - Hidden when scrolled */}
      <div className={`bg-marrakech-dark text-marrakech-cream py-2 transition-all duration-300 ${scrolled ? 'hidden' : 'hidden md:block'}`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <a href="tel:+212644005467" className="flex items-center hover:text-marrakech-gold transition">
                <FaPhone className="mr-2" />
                +2126 44 00 54 67
              </a>
              <a href="mailto:reservation@janamarrakech.com" className="flex items-center hover:text-marrakech-gold transition">
                <FaEnvelope className="mr-2" />
                reservation@janamarrakech.com
              </a>
            </div>
            <div className="text-white font-semibold">
              {t('navbar.hoursTopBar')}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className={`w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'fixed top-0 bg-white shadow-lg py-2'
            : 'sticky top-0 bg-white py-4'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="/jana-marrakech-logo.png"
                alt="Jana Marrakech"
                className={`transition-all duration-300 ${
                  scrolled ? 'h-12' : 'h-16'
                }`}
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-semibold text-lg transition-all duration-300 relative group text-marrakech-dark ${
                    location.pathname === link.path
                      ? 'text-marrakech-terracotta'
                      : 'hover:text-marrakech-terracotta'
                  }`}
                >
                  {t(link.key)}
                  <span
                    className={`absolute bottom-0 left-0 w-0 h-0.5 bg-marrakech-terracotta transition-all duration-300 group-hover:w-full ${
                      location.pathname === link.path ? 'w-full' : ''
                    }`}
                  ></span>
                </Link>
              ))}
              <Link
                to="/reservation"
                className="btn-primary"
              >
                {t('navbar.reservation')}
              </Link>

              <div className='flex items-center'>
                <button onClick={() => changeLanguage('fr')} className={language === 'fr' ? 'text-[#72201e] font-bold border-b border-b-[#72201e]' : 'text-black'}>FR</button>
                <div className='mx-2'>|</div>
                <button onClick={() => changeLanguage('en')} className={language === 'en' ? 'text-[#72201e] font-bold border-b border-b-[#72201e]' : 'text-black'}>EN</button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden text-3xl text-marrakech-dark"
            >
              {isOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden fixed top-0 right-0 h-full w-72 bg-marrakech-dark transform transition-transform duration-300 ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col p-8 space-y-6 mt-16">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-white text-lg font-semibold hover:text-marrakech-gold transition ${
                  location.pathname === link.path ? 'text-marrakech-gold' : ''
                }`}
              >
                {t(link.key)}
              </Link>
            ))}
            <Link
              to="/reservation"
              className="btn-primary text-center"
            >
              {t('navbar.reservation')}
            </Link>
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => changeLanguage('fr')}
                className={`px-3 py-2 font-semibold transition ${
                  language === 'fr'
                    ? 'text-marrakech-gold border-b-2 border-marrakech-gold'
                    : 'text-marrakech-cream hover:text-marrakech-gold'
                }`}
              >
                FR
              </button>
              <span className="text-marrakech-cream">|</span>
              <button
                onClick={() => changeLanguage('en')}
                className={`px-3 py-2 font-semibold transition ${
                  language === 'en'
                    ? 'text-marrakech-gold border-b-2 border-marrakech-gold'
                    : 'text-marrakech-cream hover:text-marrakech-gold'
                }`}
              >
                EN
              </button>
            </div>
            <div className="pt-6 border-t border-marrakech-ochre">
              <a
                href="tel:+212644005467"
                className="flex items-center text-marrakech-cream hover:text-marrakech-gold transition mb-3"
              >
                <FaPhone className="mr-2" />
                +2126 44 00 54 67
              </a>
              <a
                href="mailto:reservation@janamarrakech.com"
                className="flex items-center text-marrakech-cream hover:text-marrakech-gold transition text-sm"
              >
                <FaEnvelope className="mr-2" />
                reservation@janamarrakech.com
              </a>
            </div>
          </div>
        </div>

        {/* Overlay */}
        {isOpen && (
          <div
            onClick={() => setIsOpen(false)}
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 -z-10"
          ></div>
        )}
      </nav>
    </>
  );
};

export default Navbar;

